import { usePlaceToStayAccommodationsQuery, PlaceToStayAccommodationPlaceV2Fragment, AccommodationPlaceDisplayLocation } from '@graphql/generated';
import { addError } from '@shared/utils/logger';
import { useMemo, useEffect } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';

export const useCustomPageAccommodationData = (): Readonly<{
  customPageAccommodationsMap: ReadonlyMap<string, PlaceToStayAccommodationPlaceV2Fragment>;
  loading: boolean;
  error: Error | null;
}> => {
  const { eventInfo } = useEventInfo();

  const { data, loading, error } = usePlaceToStayAccommodationsQuery({
    variables: {
      eventId: eventInfo?.eventId || '',
      displayLocation: AccommodationPlaceDisplayLocation.CustomPage,
    },
    skip: !eventInfo?.eventId,
    fetchPolicy: 'cache-first',
    batchMode: 'fast',
  });

  const customPageAccommodationsMap = useMemo(() => {
    if (!data?.event?.accommodationPlacesForDisplayLocation) {
      return new Map<string, PlaceToStayAccommodationPlaceV2Fragment>();
    }

    const map = new Map<string, PlaceToStayAccommodationPlaceV2Fragment>();
    for (const acc of data.event.accommodationPlacesForDisplayLocation) {
      if (acc?.id) {
        map.set(acc.id, acc);
      }
    }
    return map;
  }, [data?.event?.accommodationPlacesForDisplayLocation]);

  useEffect(() => {
    if (error) {
      addError(error, {
        source: 'PlaceToStay',
        context: 'useAccommodationData',
        errorMessage: error.message,
        eventId: eventInfo?.eventId,
      });
    }
  }, [error, eventInfo?.eventId]);

  return {
    customPageAccommodationsMap,
    loading,
    error: error ? new Error(error.message) : null,
  };
};
